import type { IPickPageItem } from '../types';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toPickPageMatchKey } from '../utils';

/**
 * The picks, keyed by the page each one points at. A listed row's own id already is that key — a
 * product's GID, any other type's plain id, `home` for the storefront root — but a pick handed in by
 * the caller carries the id of the record that saved it, so both sides are reduced to the page.
 */
const toSelection = (pages: IPickPageItem[] = []): Map<string, IPickPageItem> =>
  new Map(
    pages.map((page) => [
      toPickPageMatchKey({ baseID: page.baseID, url: page.url }) ?? page.id,
      page,
    ]),
  );

export interface IUsePickPageSelectionParams {
  /** Whether the modal is on screen — the picks are read again every time it opens. */
  isOpen: boolean;
  isMultiSelect: boolean;
  /** Picks the target already holds, which the list opens on with each of them checked. */
  pickedPages?: IPickPageItem[];
  /** Resolves an id the list hands back into the page behind it. */
  resolvePage: (id: string) => IPickPageItem | undefined;
}

export interface IUsePickPageSelectionResult {
  selectedIds: string[];
  selectedPages: IPickPageItem[];
  /**
   * Keys of the picks the modal opened on. They are exempt from the "this experiment already tests
   * it" lock — unchecking one here is how it gets dropped.
   */
  pickedPageKeys: Set<string>;
  /** Whether the selection moved off the picks the modal opened on — there is nothing to confirm until it does. */
  isSelectionChanged: boolean;
  handleToggleSelectedId: (id: string) => void;
  handleSetSelectedIds: (ids: string[]) => void;
  /** Drops whatever was picked in the modal and starts from the target's own picks again. */
  handleResetSelection: () => void;
}

/**
 * What the modal currently has picked. Held as the pages themselves rather than their ids alone, so
 * a pick the list isn't showing — one made before a search narrowed it, or one the caller handed in
 * that never loads — still comes back on confirm instead of silently dropping out.
 */
export const usePickPageSelection = ({
  isOpen,
  isMultiSelect,
  pickedPages,
  resolvePage,
}: IUsePickPageSelectionParams): IUsePickPageSelectionResult => {
  const [pickedSelection, setPickedSelection] = useState(() => toSelection(pickedPages));
  const [selection, setSelection] = useState(pickedSelection);

  // Read through a ref: the caller's picks change while the modal is closed, and re-seeding on every
  // render of a new array would wipe what is being picked right now.
  const pickedPagesRef = useRef(pickedPages);
  pickedPagesRef.current = pickedPages;

  const handleResetSelection = useCallback(() => {
    const picked = toSelection(pickedPagesRef.current);

    setPickedSelection(picked);
    setSelection(picked);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    handleResetSelection();
  }, [isOpen, handleResetSelection]);

  const selectedIds = useMemo(() => [...selection.keys()], [selection]);
  const selectedPages = useMemo(() => [...selection.values()], [selection]);
  const pickedPageKeys = useMemo(() => new Set(pickedSelection.keys()), [pickedSelection]);

  const isSelectionChanged = useMemo(
    () =>
      selection.size !== pickedSelection.size || selectedIds.some((id) => !pickedSelection.has(id)),
    [selection.size, pickedSelection, selectedIds],
  );

  const handleSetSelectedIds = useCallback(
    (ids: string[]) => {
      setSelection((current) => {
        const next = new Map<string, IPickPageItem>();

        ids.forEach((id) => {
          const page = current.get(id) ?? resolvePage(id);
          if (page) next.set(id, page);
        });

        return next;
      });
    },
    [resolvePage],
  );

  // Row click. Single-select replaces the pick rather than toggling it, so a click always leaves one pick.
  const handleToggleSelectedId = useCallback(
    (id: string) => {
      setSelection((current) => {
        const page = current.get(id) ?? resolvePage(id);
        if (!page) return current;

        if (!isMultiSelect) return new Map([[id, page]]);

        const next = new Map(current);
        if (!next.delete(id)) next.set(id, page);

        return next;
      });
    },
    [isMultiSelect, resolvePage],
  );

  return {
    selectedIds,
    selectedPages,
    pickedPageKeys,
    isSelectionChanged,
    handleToggleSelectedId,
    handleSetSelectedIds,
    handleResetSelection,
  };
};
